import React, { useMemo, useRef, useEffect, useState } from "react";
import { PanelWrapper } from "../PanelWrapper";
import { Header } from "./Header";
import { useEventData } from "../DataProvider";
import { Podium } from "./Podium";
import { ResultSlip } from "./ResultSlip";

interface ResultsPanelProps {
  showPanel: boolean;
}

export function ResultsPanel({ showPanel }: ResultsPanelProps) {
  const { competitorsData } = useEventData();
  const [isCompactView, setIsCompactView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const sorted = useMemo(() => {
    return competitorsData.sort((a, b) => {
      if (!a.arrival || !b.arrival) return 0;
      return a.arrival - b.arrival;
    });
  }, [competitorsData]);

  // Check if we need to use compact view based on container height
  useEffect(() => {
    if (!containerRef.current) return;

    const checkSize = () => {
      const container = containerRef.current;
      if (container) {
        // Check container height against content needs
        // We're specifically targeting the 882x389 container scenario
        const containerHeight = container.clientHeight;
        const itemHeight = 65; // Approximate height of each ResultSlip
        const headerHeight = 60; // Approximate height of Header
        const padding = 40; // Total padding
        
        // Calculate how many items would fit in normal mode
        const availableHeight = containerHeight - headerHeight - padding;
        const fittableItems = Math.floor(availableHeight / itemHeight);
        
        // If we can't fit all competitors, enable compact view
        setIsCompactView(fittableItems < Math.min(6, competitorsData.length));
      }
    };

    // Initial check
    checkSize();
    
    // Add resize observer for dynamic adjustments
    const resizeObserver = new ResizeObserver(checkSize);
    resizeObserver.observe(containerRef.current);
    
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [competitorsData.length, showPanel]);

  return (
    <PanelWrapper showPanel={showPanel}>
      <div 
        ref={containerRef}
        className="flex items-start w-full h-full md:items-center bg-secondary bg-opacity-80"
      >
        <div className={`flex flex-col w-full h-full 
          ${isCompactView ? 'gap-0.5 py-0.5 px-2' : 'gap-1 p-2 md:gap-6 md:pt-6 lg:p-10'} 
          md:h-1/2 lg:h-full`}>
          <Header />
          <div className={`flex flex-col flex-1 ${isCompactView ? 'p-1' : 'p-4 lg:px-20'}`}>
            <div className={`flex-1 hidden w-full ${isCompactView ? 'mb-2' : 'mb-6'} lg:block`}>
              {sorted.length ? (
                <Podium competitors={sorted.slice(0, 3)} compact={isCompactView} />
              ) : null}
            </div>

            <ul className={`flex flex-col w-full ${isCompactView ? 'gap-0.5 mb-0.5' : 'gap-1 mb-1 lg:mb-2 lg:gap-2'} lg:hidden h-1/2`}>
              {sorted.length
                ? sorted
                    .slice(0, 3)
                    .map((c) => <ResultSlip competitor={c} key={c.id} compact={isCompactView} />)
                : null}
            </ul>
            <ul className={`flex flex-col w-full ${isCompactView ? 'gap-0.5' : 'gap-1 lg:gap-2'} h-1/2`}>
              {sorted.length
                ? sorted
                    .slice(3, 6)
                    .map((c) => <ResultSlip competitor={c} key={c.id} compact={isCompactView} />)
                : null}
            </ul>
          </div>
        </div>
      </div>
    </PanelWrapper>
  );
}